import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { getUfoSubstepForPolling } from "@/components/signup/utils";
import {
    ufoEventAsyncArchitecturePollingStatus,
    ufoEventConfirmationPageStatusPolling,
    ufoEventRedirectToProduct
} from "@/components/signup/utils";
import {
    IBxpWebStargateClient,
    ProductActivationStatusResponse
} from "@/libraries/bxp-web-stargate-client/src";

import { trackGetProvisioningStatusLatencyEvent } from "../../utils/analytics";
import { ConfirmationPageType } from "../use-confirmation-page-type";

import { DEFAULT_TIMEOUT } from "./types";

export const getCloudIdFromProductActivationStatuses = (
    ccpResponses: ProductActivationStatusResponse[]
): string | undefined =>
    ccpResponses
        .map(({ resourcePackages }) =>
            resourcePackages
                .map(
                    ({ resourceAris }) =>
                        resourceAris
                            .find((resourceAri) =>
                                resourceAri.startsWith(
                                    "ari:cloud:platform::site/"
                                )
                            )
                            ?.split("/")[1]
                )
                .find((e) => e)
        )
        .find((e) => e);

export const getPrimaryProduct = (products: readonly string[]) => {
    if (products.length > 0 && products[0] !== "") {
        return products[0];
    } else {
        return undefined;
    }
};

type ProgressBarStatusProps = {
    readonly startTime: number;
    readonly pivotPercentage: number;
    readonly progressBarPivotPoint: number;
    readonly slowProgressIncrement: number;
    readonly slowProgress: number;
    readonly interval?: NodeJS.Timer;
};
export function getProgressbarStatus({
    startTime,
    pivotPercentage,
    progressBarPivotPoint,
    slowProgressIncrement,
    slowProgress,
    interval
}: ProgressBarStatusProps) {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= DEFAULT_TIMEOUT) {
        // @ts-ignore
        clearInterval(interval);
        return 1;
    } else if (elapsedTime <= progressBarPivotPoint) {
        const rapidProgress =
            (pivotPercentage / progressBarPivotPoint) * elapsedTime;
        return rapidProgress;
    } else {
        slowProgress =
            slowProgress == 0
                ? pivotPercentage
                : slowProgress + slowProgressIncrement;
        return slowProgress;
    }
}
export type FireUfoEventForPollingResultProps = {
    pollingResult: "success" | "error" | "timeout";
    elapsedTime: number;
    platform: string | undefined;
    productKey: string;
    edition: string | undefined;
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    currentScreen: ConfirmationPageType;
    workflowId: string;
    error?: unknown;
};
export const fireUfoEventForPollingResult = ({
    pollingResult,
    elapsedTime,
    platform,
    productKey,
    edition,
    createAnalyticsEvent,
    currentScreen,
    workflowId,
    error
}: FireUfoEventForPollingResultProps): void => {
    try {
        const { timingKey, requestPath } = getUfoSubstepForPolling(platform);

        const errorOrigin = error
            ? `CloudStatusProvider polling ${pollingResult}`
            : undefined;

        // Performance metrics for polling duration
        ufoEventRedirectToProduct.endSubstep(timingKey, {
            ...(error ? { error } : {}),
            customData: {
                reqPath: requestPath,
                elapsedTimeMs: elapsedTime,
                workflowId,
                ...(errorOrigin ? { errorOrigin } : {})
            }
        });

        const ufoEvent = workflowId
            ? ufoEventAsyncArchitecturePollingStatus
            : ufoEventConfirmationPageStatusPolling;

        if (pollingResult === "success") {
            // SLO "good event" for cloud status polling
            ufoEvent.success({
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "succeeded",
                    reqPath: requestPath,
                    elapsedTimeMs: elapsedTime,
                    workflowId
                }
            });
            fireFrontendProvisioningCompletedEvent(
                "polling",
                productKey,
                edition,
                createAnalyticsEvent,
                workflowId
            );
        } else if (pollingResult === "error") {
            // SLO "bad event" for cloud status polling
            ufoEvent.failure({
                error,
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "errored",
                    reqPath: requestPath,
                    elapsedTimeMs: elapsedTime,
                    errorOrigin,
                    workflowId
                }
            });
        } else if (pollingResult === "timeout") {
            // SLO "bad event" for provisioning status
            ufoEvent.failure({
                error,
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "timedOut",
                    elapsedTimeMs: elapsedTime,
                    errorOrigin,
                    workflowId
                }
            });
        }

        // GasV3 event to measure polling provisioning latency
        trackGetProvisioningStatusLatencyEvent({
            createAnalyticsEvent,
            productKey,
            provisioningScreenOnComplete: currentScreen,
            fetchType: "polling",
            elapsedTimeSeconds: Math.round(elapsedTime / 1000),
            workflowId
        });
    } catch (error) {
        console.error(error);
    }
};
export const fireFrontendProvisioningCompletedEvent = (
    fetchType: string,
    productKey = "no-product-key",
    edition = "no-edition",
    createAnalyticsEvent: CreateUIAnalyticsEvent,
    workflowId: string
) => {
    const startTimeStr = sessionStorage.getItem("provisioningStartTime");

    if (startTimeStr && Number(startTimeStr)) {
        const startTimeSeconds = Number(startTimeStr) / 1000;
        const endTimeSeconds = Date.now() / 1000;
        const durationSeconds = endTimeSeconds - startTimeSeconds;
        createAnalyticsEvent({
            eventType: "track",
            action: "redirected",
            actionSubject: "frontend-provisioning-time",
            attributes: {
                startTimeSeconds,
                endTimeSeconds,
                productKey,
                edition,
                durationSeconds,
                fetchType,
                workflowId
            }
        }).fire();
    }
};
export const getAsyncActivationStatus = async (
    stargateClient: IBxpWebStargateClient,
    workflowId: string
) => {
    const response = await stargateClient.asyncActivationStatus({
        workflowId
    });

    if (response.workflowStatus === "PROVISIONING_SUCCEEDED") {
        return true;
    }

    if (response.workflowStatus === "UNRECOVERABLE_ERROR") {
        throw new Error("Activation failed");
    }

    return false;
};
